import React, { useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Divider,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Search as SearchIcon, ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllTemplates } from '../../templates/strategies';
import CreateStrategyWizard from './CreateStrategyWizard';
import AssignStrategyContent from './AssignStrategyContent';

const TemplateLibraryPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [sortBy, setSortBy] = useState('popular');
    const [createWizardOpen, setCreateWizardOpen] = useState(false);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [templateToView, setTemplateToView] = useState(null);
    const navigate = useNavigate();

    const templates = getAllTemplates();

    // Mock data for enhanced stats (in production, this would come from backend)
    const templateStats = {
        'strategy_v1_basic': { avgPnL: 850, winRate: 68, users: 32 },
        'grid_trading_advanced': { avgPnL: 2450, winRate: 74, users: 45 }
    };

    // Color mapping for different strategy types
    const getCategoryColor = (type) => {
        const colorMap = {
            'basic': '#f59e0b',      // Orange
            'grid': '#22c55e',       // Green
            'arbitrage': '#667eea',  // Purple
            'scalping': '#06b6d4',   // Cyan
            'swing': '#ef4444',      // Red
            'hedging': '#3b82f6'     // Blue
        };
        return colorMap[type] || '#64748b';
    };

    // Filter and sort templates
    const getFilteredTemplates = () => {
        let filtered = templates;

        // Search filter
        if (searchQuery) {
            filtered = filtered.filter(t =>
                t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                t.strategyType.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Type filter
        if (typeFilter !== 'all') {
            filtered = filtered.filter(t => t.strategyType === typeFilter);
        }

        // Sort
        if (sortBy === 'popular') {
            filtered = [...filtered].sort((a, b) => {
                const aStats = templateStats[a.templateId] || { users: 0 };
                const bStats = templateStats[b.templateId] || { users: 0 };
                return bStats.users - aStats.users;
            });
        } else if (sortBy === 'winrate') {
            filtered = [...filtered].sort((a, b) => {
                const aStats = templateStats[a.templateId] || { winRate: 0 };
                const bStats = templateStats[b.templateId] || { winRate: 0 };
                return bStats.winRate - aStats.winRate;
            });
        }

        return filtered;
    };

    const filteredTemplates = getFilteredTemplates();

    const handleAssignTemplate = (template) => {
        setSelectedTemplate(template);
        setAssignModalOpen(true);
    };

    const handleViewTemplate = (template) => {
        setTemplateToView(template);
        setViewModalOpen(true);
    };

    return (
        <Box sx={{
            padding: 4,
            background: 'rgba(51, 53, 57, 1)',
            minHeight: '100vh'
        }}>
            {/* Header with Back Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
                <IconButton
                    onClick={() => navigate('/adminPanel', { state: { activeTab: 'strategy-management' } })}
                    sx={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        '&:hover': { background: 'rgba(255, 255, 255, 0.2)' }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                        📚 Template Library
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', marginTop: 0.5 }}>
                        Select and assign pre-built templates to users
                    </Typography>
                </Box>
            </Box>

            {/* Search and Filters */}
            <Box sx={{ display: 'flex', gap: 2, marginBottom: 4 }}>
                {/* Search Bar */}
                <TextField
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                            </InputAdornment>
                        ),
                        sx: { color: '#ffffff' }
                    }}
                    sx={{
                        background: 'rgba(41, 44, 49, 1)',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused fieldset': { borderColor: '#667eea' }
                        }
                    }}
                />

                {/* Type Filter */}
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Type: All</InputLabel>
                    <Select
                        value={typeFilter}
                        label="Type: All"
                        onChange={(e) => setTypeFilter(e.target.value)}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    background: 'rgba(41, 43, 47, 1)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    '& .MuiMenuItem-root': {
                                        color: '#ffffff',
                                        '&:hover': {
                                            background: 'rgba(102, 126, 234, 0.2)'
                                        },
                                        '&.Mui-selected': {
                                            background: 'rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: 'rgba(102, 126, 234, 0.4)'
                                            }
                                        }
                                    }
                                }
                            }
                        }}
                        sx={{
                            background: 'rgba(41, 44, 49, 1)',
                            color: '#ffffff',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                            '.MuiSvgIcon-root': { color: '#ffffff' }
                        }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="basic">Basic</MenuItem>
                        <MenuItem value="grid">Grid</MenuItem>
                        <MenuItem value="arbitrage">Arbitrage</MenuItem>
                        <MenuItem value="scalping">Scalping</MenuItem>
                    </Select>
                </FormControl>

                {/* Sort Dropdown */}
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Sort: Popular</InputLabel>
                    <Select
                        value={sortBy}
                        label="Sort: Popular"
                        onChange={(e) => setSortBy(e.target.value)}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    background: 'rgba(41, 43, 47, 1)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    '& .MuiMenuItem-root': {
                                        color: '#ffffff',
                                        '&:hover': {
                                            background: 'rgba(102, 126, 234, 0.2)'
                                        },
                                        '&.Mui-selected': {
                                            background: 'rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: 'rgba(102, 126, 234, 0.4)'
                                            }
                                        }
                                    }
                                }
                            }
                        }}
                        sx={{
                            background: 'rgba(41, 44, 49, 1)',
                            color: '#ffffff',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                            '.MuiSvgIcon-root': { color: '#ffffff' }
                        }}
                    >
                        <MenuItem value="popular">Popular</MenuItem>
                        <MenuItem value="newest">Newest</MenuItem>
                        <MenuItem value="winrate">Win Rate</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Template Cards */}
            <Grid container spacing={3}>
                {filteredTemplates.map((template) => {
                    const stats = templateStats[template.templateId] || { avgPnL: 0, winRate: 0, users: 0 };
                    const borderColor = getCategoryColor(template.strategyType);

                    return (
                        <Grid item xs={12} sm={6} md={4} key={template.templateId}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: '12px',
                                    background: 'rgba(41, 44, 49, 1)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderTop: `4px solid ${borderColor}`,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
                                        transform: 'translateY(-4px)',
                                        borderColor: borderColor,
                                        background: 'rgba(45, 48, 53, 1)'
                                    }
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1, padding: 3 }}>
                                    {/* Template Header */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, marginBottom: 2 }}>
                                        <Typography sx={{ fontSize: '2.5rem' }}>
                                            {template.icon}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)', marginBottom: 0.5 }}>
                                                {template.name}
                                            </Typography>
                                            <Chip
                                                label={template.strategyType.charAt(0).toUpperCase() + template.strategyType.slice(1)}
                                                size="small"
                                                sx={{
                                                    background: `${borderColor}33`,
                                                    color: borderColor,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    border: `1px solid ${borderColor}`
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Template Description */}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            marginBottom: 2,
                                            lineHeight: 1.6,
                                            minHeight: '48px'
                                        }}
                                    >
                                        {template.description}
                                    </Typography>

                                    {/* Template Details */}
                                    <Box sx={{ marginBottom: 2 }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                            • Symbols: {template.parameters.find(p => p.name === 'symbols')?.defaultValue || 'Multiple'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                            • Grid: 1.5 pips | TP: 8.0 pips | SL: 15.0 pips
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                            • Version: {template.version.toUpperCase()}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ marginY: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                                    {/* Enhanced Stats */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                                                Avg P&L
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                                +${stats.avgPnL.toLocaleString()}/mo
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                                                Win Rate
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 700 }}>
                                                {stats.winRate}%
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                                                Users
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: '#06b6d4', fontWeight: 700 }}>
                                                {stats.users}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>

                                {/* Action Buttons */}
                                <Box sx={{ padding: 3, paddingTop: 0, display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleViewTemplate(template)}
                                        sx={{
                                            flex: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: 'rgba(255, 255, 255, 0.2)',
                                            color: '#ffffff',
                                            '&:hover': {
                                                borderColor: borderColor,
                                                background: `${borderColor}22`
                                            }
                                        }}
                                    >
                                        👁️ View
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleAssignTemplate(template)}
                                        sx={{
                                            flex: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: `linear-gradient(135deg, ${borderColor} 0%, ${borderColor}dd 100%)`,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${borderColor}dd 0%, ${borderColor} 100%)`,
                                                transform: 'scale(1.02)'
                                            },
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        Assign to User
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Empty State */}
            {filteredTemplates.length === 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        padding: 8,
                        background: 'rgba(41, 44, 49, 1)',
                        borderRadius: '12px',
                        border: '2px dashed rgba(255, 255, 255, 0.2)'
                    }}
                >
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: 1 }}>
                        No Templates Found
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        Try adjusting your search or filters
                    </Typography>
                </Box>
            )}

            {/* Assign Strategy Modal (Simple Assignment) */}
            <Dialog
                open={assignModalOpen}
                onClose={() => {
                    setAssignModalOpen(false);
                    setSelectedTemplate(null);
                }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        background: 'rgba(51, 53, 57, 1)',
                        maxHeight: '90vh'
                    }
                }}
            >
                <AssignStrategyContent
                    template={selectedTemplate}
                    onBack={() => {
                        setAssignModalOpen(false);
                        setSelectedTemplate(null);
                    }}
                />
            </Dialog>

            {/* View Template Modal */}
            <Dialog
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        background: 'rgba(41, 43, 47, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }
                }}
            >
                {templateToView && (
                    <>
                        <DialogTitle sx={{
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            padding: 3,
                            background: 'rgba(41, 43, 47, 1)'
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography sx={{ fontSize: '2rem' }}>
                                        {templateToView.icon}
                                    </Typography>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                            {templateToView.name}
                                        </Typography>
                                        <Chip
                                            label={templateToView.strategyType.charAt(0).toUpperCase() + templateToView.strategyType.slice(1)}
                                            size="small"
                                            sx={{
                                                background: `${getCategoryColor(templateToView.strategyType)}33`,
                                                color: getCategoryColor(templateToView.strategyType),
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                                border: `1px solid ${getCategoryColor(templateToView.strategyType)}`
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <IconButton onClick={() => setViewModalOpen(false)} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>

                        <DialogContent sx={{ padding: 3, background: 'rgba(41, 43, 47, 1)' }}>
                            {/* Description */}
                            <Box sx={{ marginBottom: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: '#667eea', marginBottom: 1, fontWeight: 600 }}>
                                    Description
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                                    {templateToView.description}
                                </Typography>
                            </Box>

                            <Divider sx={{ marginY: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                            {/* Parameters */}
                            <Box sx={{ marginBottom: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: '#667eea', marginBottom: 2, fontWeight: 600 }}>
                                    Configuration Parameters
                                </Typography>
                                <Grid container spacing={2}>
                                    {templateToView.parameters.map((param, index) => (
                                        <Grid item xs={12} sm={6} key={index}>
                                            <Box sx={{
                                                padding: 2,
                                                background: 'rgba(102, 126, 234, 0.05)',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(102, 126, 234, 0.2)'
                                            }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                    {param.label || param.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                                    {param.defaultValue || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem' }}>
                                                    {param.type}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>

                            {/* Stats */}
                            <Divider sx={{ marginY: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#667eea', marginBottom: 2, fontWeight: 600 }}>
                                    Performance Stats
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center', padding: 2, background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                Avg P&L
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                                +${(templateStats[templateToView.templateId]?.avgPnL || 0).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center', padding: 2, background: 'rgba(102, 126, 234, 0.1)', borderRadius: '8px' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                Win Rate
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700 }}>
                                                {templateStats[templateToView.templateId]?.winRate || 0}%
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center', padding: 2, background: 'rgba(6, 182, 212, 0.1)', borderRadius: '8px' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                Active Users
                                            </Typography>
                                            <Typography variant="h6" sx={{ color: '#06b6d4', fontWeight: 700 }}>
                                                {templateStats[templateToView.templateId]?.users || 0}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{
                            padding: 3,
                            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                            background: 'rgba(41, 43, 47, 1)',
                            gap: 2
                        }}>
                            <Button
                                onClick={() => setViewModalOpen(false)}
                                sx={{
                                    textTransform: 'none',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setViewModalOpen(false);
                                    handleAssignTemplate(templateToView);
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                                    }
                                }}
                            >
                                Assign to User
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default TemplateLibraryPage;
